import { AI_PROFILES, AI_REASONING_EFFORTS, type AiProviderId } from '@fos/shared/contracts/ai';
import { type AiProviderOption, type AiSettingsView, COPILOT_MESSAGES } from '@fos/shared/contracts/copilot';
import {
  AI_CAPABILITY_LABELS,
  AI_MODEL_PLACEHOLDERS,
  AI_PROFILE_DESCRIPTIONS,
  AI_PROFILE_LABELS,
  AI_PROVIDER_STATE_LABELS,
  AI_PROVIDER_STATE_TONES,
  AI_REASONING_EFFORT_LABELS,
  aiProviderLabel,
} from '@fos/shared/labels';
import { Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { ContextBanner, FormMessage } from '../../../ui/Blocks';
import { Button, IconButton } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Select, type SelectOption } from '../../../ui/Select';
import { ErrorState, ListSkeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';
import {
  type AiSettingsForm,
  capabilitiesText,
  errorsFromIssues,
  type FormErrors,
  formFromView,
  formSignature,
  type PriceRow,
  priceRow,
  type ReasoningChoice,
  toInput,
} from './ai-settings-ui';

const REASONING_OPTIONS: SelectOption<ReasoningChoice>[] = [
  { value: 'default', label: 'Domyślny modelu' },
  ...AI_REASONING_EFFORTS.map((e) => ({ value: e, label: AI_REASONING_EFFORT_LABELS[e] })),
];

/**
 * Ustawienia → AI i pamięć → Model i limity (MVP §12; DECISIONS: Manager zarządza limitami AI — tak): włączenie AI,
 * klasyfikacja maili, dostawca (symulacja / Anthropic / OpenAI — realny tylko podłączony kluczem w Integracjach,
 * inaczej „Niepodłączony, działa tryb symulacji”), modele per profil (tekst wpisany przez osobę — w kodzie tylko
 * przykład w podpowiedzi) z poziomem rozumowania (Fala 9: dostawca z tą możliwością, np. OpenAI), budżet miesięczny
 * workspace'u i domyślny limit osoby, limit iteracji narzędzi i czasu, cennik modeli (PLN za 1 mln tokenów: wejście,
 * wejście z cache, wyjście), deklarowane możliwości adapterów. Zapisany dostawca bez klucza (np. preset OpenAI) zostaje
 * do wyboru — do wpisania klucza działa symulacja z komunikatem „brak klucza”. Zmiana dostawcy ani modelu nie zmienia
 * danych CRM. Kwoty tylko z „Widzi stawki i kwoty”; zapis: właściciel, admin, manager z tą flagą.
 */
export function AiModelSection() {
  const settings = trpc.ai.getSettings.useQuery();
  if (settings.isPending) {
    return (
      <Section>
        <ListSkeleton rows={4} />
      </Section>
    );
  }
  if (settings.isError) {
    return (
      <Section>
        <ErrorState
          title={appErrorCode(settings.error) === 'FORBIDDEN' ? 'Brak dostępu do ustawień AI' : undefined}
          onRetry={() => void settings.refetch()}
          retrying={settings.isFetching}
        />
      </Section>
    );
  }
  return <SettingsForm view={settings.data} />;
}

function SettingsForm({ view }: { view: AiSettingsView }) {
  const utils = trpc.useUtils();
  const save = trpc.ai.updateSettings.useMutation();
  const [form, setForm] = useState<AiSettingsForm>(() => formFromView(view));
  const [errors, setErrors] = useState<FormErrors>({});
  const [conflict, setConflict] = useState(false);
  const baseKey = `${view.version ?? 'env'}`;
  const readOnly = !view.canEdit;
  const providerGroup = useId();

  // nowa wersja z serwera (po zapisie albo „Wczytaj ponownie”) = świeży formularz
  // biome-ignore lint/correctness/useExhaustiveDependencies: tylko przy zmianie wersji ustawień
  useEffect(() => {
    setForm(formFromView(view));
    setErrors({});
    setConflict(false);
  }, [baseKey]);

  const initial = useMemo(() => formSignature(formFromView(view)), [view]);
  const dirty = formSignature(form) !== initial;
  const set = <K extends keyof AiSettingsForm>(key: K, value: AiSettingsForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined, form: undefined }));
  };

  const selected = view.providers.find((p) => p.id === form.provider);
  const reasoningSupported = form.provider !== 'fake' && selected?.capabilities?.reasoningEffort === true;
  const priceGrid = readOnly ? PRICE_GRID_READONLY : PRICE_GRID;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const { input, errors: local } = toInput(form, view.version, {
      showMoney: view.showMoney,
      reasoning: form.provider === 'fake' || reasoningSupported,
    });
    setErrors(local);
    if (!input) return;
    try {
      const next = await save.mutateAsync(input);
      utils.ai.getSettings.setData(undefined, next);
      void utils.copilot.status.invalidate();
      toast.success('Zapisano ustawienia AI');
    } catch (err) {
      if (appErrorCode(err) === 'CONFLICT') {
        setConflict(true);
        return;
      }
      const issues = validationIssues(err);
      setErrors(issues.length ? errorsFromIssues(issues) : { form: errorMessage(err, 'Nie udało się zapisać.') });
    }
  };

  const reload = async () => {
    await utils.ai.getSettings.invalidate();
    setConflict(false);
  };

  const active = view.active;
  const required = [...new Set(Object.values(view.required).flat())];

  return (
    <form
      onSubmit={(e) => void submit(e)}
      noValidate
      aria-label="Model i limity"
      className="flex flex-col gap-4 md:gap-6"
    >
      <div className="flex flex-col gap-2">
        {active.simulated && active.provider !== 'fake' ? (
          <ContextBanner tone="info">
            {active.message ??
              `${aiProviderLabel(active.provider)}: brak klucza API. Do tego czasu działa tryb symulacji.`}{' '}
            <Link to="/ustawienia/integracje" className="text-accent hover:underline">
              Integracje
            </Link>
          </ContextBanner>
        ) : active.simulated ? (
          <ContextBanner tone="info">
            Działa tryb symulacji: odpowiedzi AI daje adapter testowy, nic nie trafia do dostawcy. Realnego dostawcę
            podłączysz kluczem API w{' '}
            <Link to="/ustawienia/integracje" className="text-accent hover:underline">
              Integracjach
            </Link>
            .
          </ContextBanner>
        ) : active.state !== 'connected' ? (
          <ContextBanner tone="warning">{active.message ?? 'Dostawca AI jest niedostępny.'}</ContextBanner>
        ) : null}
        {!form.enabled && <ContextBanner tone="warning">{COPILOT_MESSAGES.disabled}</ContextBanner>}
        {readOnly && (
          <p className="text-caption text-fg-muted">
            Zmienia właściciel, administrator albo manager z uprawnieniem „Widzi stawki i kwoty”.
          </p>
        )}
      </div>

      <Section aria-label="Działanie" className="flex flex-col gap-3">
        <SectionHeader tone="strong" title="Działanie" />
        <Checkbox
          checked={form.enabled}
          onCheckedChange={(v) => set('enabled', v)}
          disabled={readOnly}
          label="AI włączone"
        />
        <p className="-mt-1 pl-6 text-caption text-fg-muted">
          Wyłączone AI nie blokuje pracy: zadania, czas, poczta i Akceptacje działają ręcznie.
        </p>
        <Checkbox
          checked={form.classifyIncoming}
          onCheckedChange={(v) => set('classifyIncoming', v)}
          disabled={readOnly}
          label="Oceniaj ważność przychodzących maili"
        />
        <p className="-mt-1 pl-6 text-caption text-fg-muted">
          Wyłączone: maile trafiają do Inboxu jako „Zwykłe”. „Pilne” tylko przy wysokiej pewności i zgłoszeniu awarii.
        </p>
      </Section>

      <Section aria-label="Dostawca" className="flex flex-col">
        <SectionHeader tone="strong" title="Dostawca" />
        <fieldset disabled={readOnly} className="flex flex-col">
          <legend className="sr-only">Dostawca AI</legend>
          {view.providers.map((p) => (
            <ProviderRow
              key={p.id}
              option={p}
              // zapisany dostawca zostaje do wyboru także bez klucza (preset przed wpisaniem klucza)
              selectable={p.selectable || p.id === view.fields.provider}
              group={providerGroup}
              checked={form.provider === p.id}
              onSelect={() => set('provider', p.id as AiProviderId)}
            />
          ))}
        </fieldset>
        {errors.provider && (
          <p role="alert" className="mt-2 text-caption text-danger">
            {errors.provider}
          </p>
        )}
        <p className="mt-2 text-caption text-fg-muted">
          Copilot wymaga: {required.map((c) => AI_CAPABILITY_LABELS[c]).join(', ')}. Dostawca bez nich zgłosi jawny błąd
          — bez cichej zamiany na innego. Bez skonfigurowanej zgody i budżetu nie ma płatnego zastępstwa.
        </p>
      </Section>

      <Section aria-label="Modele" className="flex flex-col gap-4">
        <SectionHeader tone="strong" title="Modele" />
        {form.provider === 'fake' && (
          <p className="-mt-2 text-caption text-fg-muted">W symulacji model nie jest potrzebny.</p>
        )}
        {AI_PROFILES.map((p) => (
          <div key={p} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_184px] sm:items-start sm:gap-3">
            <Field
              label={`${AI_PROFILE_LABELS[p]}${p === 'exec' ? ' (opcjonalnie)' : ''}`}
              hint={AI_PROFILE_DESCRIPTIONS[p]}
              error={errors[`profiles.${p}.model`]}
            >
              <Input
                mono
                value={form.models[p]}
                disabled={readOnly || form.provider === 'fake'}
                placeholder={AI_MODEL_PLACEHOLDERS[form.provider] ?? 'identyfikator modelu'}
                maxLength={200}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((f) => ({ ...f, models: { ...f.models, [p]: value } }));
                  setErrors((er) => ({ ...er, [`profiles.${p}.model`]: undefined }));
                }}
              />
            </Field>
            <Field label="Rozumowanie">
              <Select<ReasoningChoice>
                aria-label={`Poziom rozumowania — ${AI_PROFILE_LABELS[p]}`}
                value={reasoningSupported ? form.reasoning[p] : 'default'}
                options={REASONING_OPTIONS}
                disabled={readOnly || !reasoningSupported}
                onValueChange={(value) => setForm((f) => ({ ...f, reasoning: { ...f.reasoning, [p]: value } }))}
              />
            </Field>
          </div>
        ))}
        {form.provider !== 'fake' && (
          <p className="text-caption text-fg-muted">
            {reasoningSupported
              ? 'Wyższy poziom rozumowania = dokładniej, ale wolniej i drożej (tokeny rozumowania liczą się jak wyjście). „Domyślny modelu” — bez ustawienia.'
              : `${aiProviderLabel(form.provider)}: poziom rozumowania nie jest obsługiwany — model działa z ustawieniem domyślnym.`}
          </p>
        )}
      </Section>

      <Section aria-label="Limity" className="flex flex-col gap-4">
        <SectionHeader tone="strong" title="Limity" />
        {view.showMoney ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Budżet miesięczny firmy (zł)"
              hint="Puste = bez limitu. Po przekroczeniu AI się zatrzymuje."
              error={errors.monthlyBudgetPln}
            >
              <Input
                mono
                inputMode="decimal"
                value={form.monthlyBudgetPln}
                disabled={readOnly}
                placeholder="bez limitu"
                onChange={(e) => set('monthlyBudgetPln', e.target.value)}
              />
            </Field>
            <Field
              label="Domyślny limit osoby (zł / mies.)"
              hint="Własny limit osoby ustawisz w Zespół → osoba."
              error={errors.perUserBudgetPln}
            >
              <Input
                mono
                inputMode="decimal"
                value={form.perUserBudgetPln}
                disabled={readOnly}
                placeholder="bez limitu"
                onChange={(e) => set('perUserBudgetPln', e.target.value)}
              />
            </Field>
          </div>
        ) : (
          <p className="text-caption text-fg-muted">Budżety i ceny widzą osoby z „Widzi stawki i kwoty”.</p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Limit kroków narzędzi"
            hint="Ile razy AI może sięgnąć po narzędzia w jednej odpowiedzi (1–20)."
            error={errors.maxToolIterations}
          >
            <Input
              mono
              inputMode="numeric"
              value={form.maxToolIterations}
              disabled={readOnly}
              onChange={(e) => set('maxToolIterations', e.target.value)}
            />
          </Field>
          <Field
            label="Limit czasu (s)"
            hint="Dłuższe zadanie AI zostanie przerwane (5–600 s)."
            error={errors.timeoutMs}
          >
            <Input
              mono
              inputMode="numeric"
              value={form.timeoutSec}
              disabled={readOnly}
              onChange={(e) => {
                set('timeoutSec', e.target.value);
                setErrors((er) => ({ ...er, timeoutMs: undefined }));
              }}
            />
          </Field>
        </div>
      </Section>

      {view.showMoney && (
        <Section aria-label="Cennik" className="flex flex-col gap-3">
          <SectionHeader
            tone="strong"
            title="Cennik modeli (zł za 1 mln tokenów)"
            action={
              readOnly ? undefined : (
                <Button
                  variant="ghost"
                  size="s"
                  icon={Plus}
                  onClick={() => setForm((f) => ({ ...f, prices: [...f.prices, priceRow()] }))}
                >
                  Dodaj model
                </Button>
              )
            }
          />
          {form.prices.length === 0 ? (
            <p className="text-caption text-fg-muted">
              Bez ceny koszt realnego dostawcy liczy się jako 0 zł i budżet go nie zatrzyma — dodaj ceny używanych
              modeli. Symulacja jest bezpłatna.
            </p>
          ) : (
            <>
              <div aria-hidden className={cn('hidden gap-3 text-caption text-fg-muted sm:grid', priceGrid)}>
                <span>Model</span>
                <span>Wejście</span>
                <span>Wejście z cache</span>
                <span>Wyjście</span>
              </div>
              <ul aria-label="Cennik" className="flex flex-col gap-3">
                {form.prices.map((row, i) => (
                  <li key={row.key} className={cn('grid items-start gap-2 sm:gap-3', priceGrid)}>
                    <Field error={errors[`prices.${i}.model`]}>
                      <Input
                        mono
                        aria-label={`Model ${i + 1}`}
                        value={row.model}
                        disabled={readOnly}
                        placeholder="identyfikator modelu"
                        onChange={(e) => updateRow(setForm, i, { model: e.target.value })}
                      />
                    </Field>
                    <div className="grid grid-cols-3 gap-2 sm:contents">
                      {PRICE_FIELDS.map((pf) => (
                        <PriceInput
                          key={pf.field}
                          row={row}
                          index={i}
                          {...pf}
                          error={errors[`prices.${i}.${pf.field}`]}
                          readOnly={readOnly}
                          onChange={(value) => updateRow(setForm, i, { [pf.field]: value })}
                        />
                      ))}
                    </div>
                    {!readOnly && (
                      <IconButton
                        icon={Trash2}
                        size={32}
                        label={`Usuń model ${row.model || i + 1} z cennika`}
                        onClick={() => setForm((f) => ({ ...f, prices: f.prices.filter((r) => r.key !== row.key) }))}
                        className="justify-self-end sm:justify-self-auto"
                      />
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-caption text-fg-muted">
                Wejście z cache — tańsza cena powtórzonego początku zapytania (np. stałej instrukcji); puste = cena
                wejścia. Tokeny rozumowania liczą się jako wyjście.
              </p>
            </>
          )}
          {errors.priceTable && (
            <p role="alert" className="text-caption text-danger">
              {errors.priceTable}
            </p>
          )}
        </Section>
      )}

      {conflict && (
        <div className="flex flex-wrap items-center gap-3">
          <FormMessage>Ustawienia zmieniły się w międzyczasie. Wczytaj najnowsze i zapisz ponownie.</FormMessage>
          <Button variant="secondary" onClick={() => void reload()}>
            Wczytaj ponownie
          </Button>
        </div>
      )}
      {errors.form && <FormMessage>{errors.form}</FormMessage>}

      {!readOnly && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {dirty && (
            <Button variant="ghost" onClick={() => setForm(formFromView(view))} disabled={save.isPending}>
              Przywróć
            </Button>
          )}
          <Button variant="primary" type="submit" loading={save.isPending} disabled={!dirty}>
            Zapisz
          </Button>
        </div>
      )}
    </form>
  );
}

const PRICE_GRID = 'sm:grid-cols-[minmax(0,1fr)_104px_104px_104px_32px]';
const PRICE_GRID_READONLY = 'sm:grid-cols-[minmax(0,1fr)_104px_104px_104px]';

type PriceField = 'inputPer1M' | 'cachedInputPer1M' | 'outputPer1M';

const PRICE_FIELDS: { field: PriceField; label: string; placeholder: string }[] = [
  { field: 'inputPer1M', label: 'Wejście', placeholder: 'wejście' },
  { field: 'cachedInputPer1M', label: 'Wejście z cache', placeholder: 'jak wejście' },
  { field: 'outputPer1M', label: 'Wyjście', placeholder: 'wyjście' },
];

/** Cena w cenniku: na telefonie z podpisem nad polem (nagłówek kolumn jest od 640 px). */
function PriceInput({
  row,
  index,
  field,
  label,
  placeholder,
  error,
  readOnly,
  onChange,
}: {
  row: PriceRow;
  index: number;
  field: PriceField;
  label: string;
  placeholder: string;
  error: string | undefined;
  readOnly: boolean;
  onChange: (value: string) => void;
}) {
  return (
    // od 640 px podpis zastępuje nagłówek kolumn (pierwszy element pola = wiersz etykiety)
    <Field error={error} label={label} className="min-w-0 sm:[&>div:first-child]:hidden">
      <Input
        mono
        inputMode="decimal"
        aria-label={`${label}, model ${index + 1}`}
        value={row[field]}
        disabled={readOnly}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

function updateRow(
  setForm: (fn: (f: AiSettingsForm) => AiSettingsForm) => void,
  index: number,
  patch: Partial<AiSettingsForm['prices'][number]>,
) {
  setForm((f) => ({ ...f, prices: f.prices.map((r, i) => (i === index ? { ...r, ...patch } : r)) }));
}

function ProviderRow({
  option,
  selectable,
  group,
  checked,
  onSelect,
}: {
  option: AiProviderOption;
  /** można wybrać: podłączony albo już zapisany (Fala 9) */
  selectable: boolean;
  group: string;
  checked: boolean;
  onSelect: () => void;
}) {
  const disabled = !selectable;
  return (
    <label
      className={cn(
        'flex min-h-[60px] items-start gap-3 border-b border-line py-3 has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-accent',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      )}
    >
      <input
        type="radio"
        name={group}
        value={option.id}
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
        aria-label={`${aiProviderLabel(option.id)} — ${AI_PROVIDER_STATE_LABELS[option.state]}`}
        className="sr-only"
      />
      <span
        aria-hidden
        className={cn(
          'mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full border-[1.5px]',
          checked ? 'border-accent' : 'border-line-strong',
          disabled && 'opacity-50',
        )}
      >
        {checked && <span className="size-2.5 rounded-full bg-accent" />}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={cn('text-body-s font-medium', disabled ? 'text-fg-muted' : 'text-fg')}>
          {aiProviderLabel(option.id)}
        </span>
        <span className="text-caption text-fg-muted">
          {capabilitiesText(option.capabilities)}
          {option.source === 'env'
            ? ' · klucz instalacji'
            : option.source === 'integration'
              ? ' · klucz w Integracjach'
              : ''}
        </span>
        {option.message && (
          <span className="text-caption text-fg-secondary">
            {option.message}{' '}
            {option.state === 'not_connected' && (
              <Link to="/ustawienia/integracje" className="text-accent hover:underline">
                Integracje
              </Link>
            )}
          </span>
        )}
      </span>
      <Status tone={AI_PROVIDER_STATE_TONES[option.state]} className="mt-0.5">
        {AI_PROVIDER_STATE_LABELS[option.state]}
      </Status>
    </label>
  );
}
